/**
 * R3F 舞台层：灯光/阴影/取景 + 魔方姿态控制（拖拽/视角预设/Hero 自转视差）
 *
 * 视角欧拉角（YXZ 序）沿用已调好的数值：x 正角 = 顶面朝相机，负角 = 底面朝相机。
 * 别改符号——这是旧版踩坑验证过的约定。
 */
import { useEffect, useImperativeHandle, useRef, type ReactNode, type Ref } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { ContactShadows } from '@react-three/drei';
import * as THREE from 'three';
import type { CubeKernel } from './cubeKernel';

export type ViewName = 'top' | 'front' | 'bottom';

export const VIEWS: Record<ViewName, { x: number; y: number }> = {
  top: { x: 0.55, y: 0.62 },
  front: { x: 0.1, y: 0.55 },
  bottom: { x: -1.15, y: 0.62 },
};

export interface CubeRigHandle {
  setView: (v: ViewName) => void;
}

interface RigProps {
  kernel: CubeKernel;
  mode: 'demo' | 'hero' | 'play';
  initialView?: ViewName;
  float?: boolean;
  handleRef?: Ref<CubeRigHandle>;
  scale?: number;
}

const TWO_PI = Math.PI * 2;
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export function CubeRig({ kernel, mode, initialView = 'top', float = true, handleRef, scale = 1 }: RigProps) {
  const outer = useRef<THREE.Group>(null!);
  const inner = useRef<THREE.Group>(null!);
  const { gl } = useThree();

  const st = useRef({
    dragging: false,
    lastX: 0,
    lastY: 0,
    hover: false,
    parX: 0,
    parY: 0,
    tween: null as null | { fromX: number; fromY: number; dx: number; dy: number; start: number; ms: number },
  });

  // 初始姿态
  useEffect(() => {
    const g = inner.current;
    g.rotation.order = 'YXZ';
    const v = VIEWS[initialView];
    g.rotation.x = v.x;
    g.rotation.y = v.y;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tweenTo = (rx: number, ry: number, ms = 420) => {
    const g = inner.current;
    let fromY = g.rotation.y % TWO_PI;
    if (fromY > Math.PI) fromY -= TWO_PI;
    if (fromY < -Math.PI) fromY += TWO_PI;
    let dy = ry - fromY;
    if (dy > Math.PI) dy -= TWO_PI;
    if (dy < -Math.PI) dy += TWO_PI;
    st.current.tween = { fromX: g.rotation.x, fromY, dx: rx - g.rotation.x, dy, start: performance.now(), ms };
  };

  useImperativeHandle(handleRef, () => ({
    setView: (v: ViewName) => tweenTo(VIEWS[v].x, VIEWS[v].y),
  }));

  // 拖拽（demo/play 模式）与 Hero 的 hover/视差监听
  useEffect(() => {
    const el = gl.domElement;
    const s = st.current;

    const down = (e: PointerEvent) => {
      if (mode === 'hero') return;
      el.setPointerCapture(e.pointerId);
      s.dragging = true;
      s.lastX = e.clientX;
      s.lastY = e.clientY;
      s.tween = null;
    };
    const move = (e: PointerEvent) => {
      if (mode === 'hero') {
        const rect = el.getBoundingClientRect();
        s.parX = ((e.clientY - rect.top) / rect.height - 0.5) * 0.36;
        s.parY = ((e.clientX - rect.left) / rect.width - 0.5) * 0.55;
        return;
      }
      if (!s.dragging) return;
      const g = inner.current;
      g.rotation.y += (e.clientX - s.lastX) * 0.01;
      g.rotation.x = clamp(g.rotation.x + (e.clientY - s.lastY) * 0.01, -1.45, 1.45);
      s.lastX = e.clientX;
      s.lastY = e.clientY;
    };
    const up = () => {
      s.dragging = false;
    };
    const enter = () => {
      s.hover = true;
    };
    const leave = () => {
      s.hover = false;
      s.dragging = false;
      s.parX = 0;
      s.parY = 0;
    };

    el.style.touchAction = 'none';
    el.style.cursor = mode === 'hero' ? 'default' : 'grab';
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    el.addEventListener('pointerenter', enter);
    el.addEventListener('pointerleave', leave);
    return () => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      el.removeEventListener('pointerenter', enter);
      el.removeEventListener('pointerleave', leave);
    };
  }, [gl, mode]);

  useFrame((state, delta) => {
    const g = inner.current;
    const o = outer.current;
    const s = st.current;

    // 视角 tween
    if (s.tween) {
      const t = Math.min(1, (performance.now() - s.tween.start) / s.tween.ms);
      const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      g.rotation.x = s.tween.fromX + s.tween.dx * e;
      g.rotation.y = s.tween.fromY + s.tween.dy * e;
      if (t >= 1) s.tween = null;
    }

    if (mode === 'hero') {
      // 悬停暂停自转；鼠标视差轻微改变朝向
      if (!s.hover) g.rotation.y += delta * 0.35;
      const baseX = 0.42;
      g.rotation.x += (baseX + s.parX - g.rotation.x) * Math.min(1, delta * 6);
      if (s.hover) {
        const targetY = g.rotation.y + s.parY * 0.06;
        g.rotation.y += (targetY - g.rotation.y) * Math.min(1, delta * 6);
      }
    }

    // 悬浮呼吸
    if (float) {
      o.position.y = Math.sin(state.clock.elapsedTime * 0.9) * 0.07;
    }
  });

  return (
    <group ref={outer} scale={scale}>
      <group ref={inner}>
        <primitive object={kernel.root} />
      </group>
    </group>
  );
}

/** 标准魔方画布：相机 + 灯光（主光/半球光/rim 背光/底光）+ 接触阴影 */
export function CubeCanvas({
  children,
  shadow = true,
  className,
}: {
  children: ReactNode;
  shadow?: boolean;
  className?: string;
}) {
  return (
    <Canvas
      className={className}
      dpr={[1, 2]}
      camera={{ position: [5.2, 4.6, 6.4], fov: 40 }}
      gl={{ antialias: true, alpha: true }}
    >
      <ambientLight intensity={0.5} />
      <hemisphereLight args={['#ffffff', '#1c2028', 0.65]} />
      <directionalLight position={[5, 8, 6]} intensity={1.6} />
      <directionalLight position={[-6, 3, -7]} intensity={1.0} color="#66c8ff" />
      <directionalLight position={[2, -6, 4]} intensity={0.55} />
      {shadow && (
        <ContactShadows position={[0, -2.1, 0]} opacity={0.5} scale={9.5} blur={2.6} far={3.4} />
      )}
      {children}
    </Canvas>
  );
}
